import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a shipping progress bar.
 * Shows progress towards free gift threshold and dynamic messaging.
 *
 * The threshold is read from the `data-threshold` attribute (in cents),
 * rendered by Liquid, so there is a single source of truth.
 *
 * Progress is measured against the cart's final total price (after all
 * discounts) — never the compare-at price — so discounted items only
 * count what the customer actually pays.
 *
 * @extends {Component}
 */
class ShippingProgressBar extends Component {
  static DEFAULT_THRESHOLD = 5500; // $55.00 in cents

  connectedCallback() {
    super.connectedCallback();

    this.updateProgress();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * The free gift threshold in cents, from the data-threshold attribute.
   * @returns {number}
   */
  get threshold() {
    const value = parseInt(this.dataset.threshold || '', 10);
    return isNaN(value) ? ShippingProgressBar.DEFAULT_THRESHOLD : value;
  }

  /**
   * Handles cart update events
   * @param {CartUpdateEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    // Prefer the authoritative cart total from the event payload (in cents,
    // already net of discounts) over parsing text from the DOM.
    const totalPrice = event?.detail?.resource?.total_price;
    this.updateProgress(typeof totalPrice === 'number' ? totalPrice : undefined);
  };

  /**
   * Updates the progress bar and message based on current cart total
   * @param {number} [cartTotal] - Cart total in cents. Falls back to parsing the DOM.
   */
  updateProgress(cartTotal) {
    if (typeof cartTotal !== 'number') {
      const cartTotalElement = document.querySelector('[data-cart-subtotal]');
      if (!cartTotalElement) return;

      // The [data-cart-subtotal] element renders cart.total_price, which is
      // the final discounted total (not compare-at prices).
      cartTotal = this.#parseMoney(cartTotalElement.textContent || '0');
    }

    const progressBar = this.querySelector('.shipping-progress-bar__fill');
    const messageElement = this.querySelector('.shipping-progress-bar__message');

    if (!progressBar || !messageElement) return;

    const threshold = this.threshold;

    // Calculate progress percentage
    const percentage = Math.min((cartTotal / threshold) * 100, 100);
    progressBar.style.width = `${percentage}%`;

    // Update message from Liquid-rendered data attributes when provided
    const amountRemaining = threshold - cartTotal;
    const incompleteTemplate =
      this.dataset.messageIncomplete ||
      'Add [amount] more to unlock <strong>FREE Shampoo + Conditioner!</strong>';
    const completeTemplate =
      this.dataset.messageComplete ||
      "<strong>🎉 You've unlocked a FREE Shampoo + Conditioner!</strong>";

    if (amountRemaining <= 0) {
      messageElement.innerHTML = completeTemplate.replaceAll('[amount]', this.#formatMoney(0));
      this.classList.add('shipping-progress-bar--complete');
    } else {
      const amountRemainingFormatted = this.#formatMoney(amountRemaining);
      messageElement.innerHTML = incompleteTemplate.replaceAll(
        '[amount]',
        `<strong>${amountRemainingFormatted}</strong>`
      );
      this.classList.remove('shipping-progress-bar--complete');
    }
  }

  /**
   * Parses money string to cents
   * @param {string} moneyString - The money string (e.g., "$45.00" or "$45")
   * @returns {number} The amount in cents
   */
  #parseMoney(moneyString) {
    // Remove currency symbols, commas, and spaces
    const cleanString = moneyString.replace(/[$,\s]/g, '');
    const amount = parseFloat(cleanString);
    return isNaN(amount) ? 0 : Math.round(amount * 100);
  }

  /**
   * Formats cents to money string
   * @param {number} cents - The amount in cents
   * @returns {string} The formatted money string
   */
  #formatMoney(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  }
}

if (!customElements.get('shipping-progress-bar')) {
  customElements.define('shipping-progress-bar', ShippingProgressBar);
}
